'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { HeroBackground } from './HeroBackground';

const CAL_URL = 'https://cal.com/krystof-olik-i0zgwb/30min';

/** `heroBg` swaps the flat dark section for the full-height blue hero
 *  backdrop — used on the standalone /contact page so it matches the other
 *  pages' heroes. The homepage keeps the plain dark CTA (no prop). */
export function CTA({ heroBg = false }: { heroBg?: boolean }) {
  const t = useTranslations('cta');
  const tCommon = useTranslations('common');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [shouldMountIframe, setShouldMountIframe] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // `loading="lazy"` on iframes is unreliable across browsers and Lighthouse
  // still measures the Cal.com bundle (~1.5s main-thread cost, ~400ms TBT) as
  // part of the initial home-page load. IntersectionObserver-gated mount
  // guarantees zero Cal.com cost until the user scrolls within ~400px of the
  // booking section. On /contact the section is in the initial viewport so the
  // observer fires immediately after hydration — effectively eager there.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Defer the fallback mount to a microtask — React 19's
      // `react-hooks/set-state-in-effect` rule rightly flags synchronous
      // setState inside an effect body as a cascading-render smell.
      queueMicrotask(() => setShouldMountIframe(true));
      return;
    }
    const io = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        setShouldMountIframe(true);
        io.disconnect();
      }
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const embedSrc = `${CAL_URL}?embed=true&theme=dark&hideEventTypeDetails=false`;

  return (
    <section
      id="contact"
      data-header-dark
      className={`cv-auto py-24 px-[10%] ${
        heroBg
          ? 'relative overflow-hidden min-h-screen flex items-center'
          : 'bg-background-secondary'
      }`}
    >
      {heroBg && <HeroBackground />}
      <div className={`relative z-10 w-full max-w-6xl grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-start ${heroBg ? 'mx-auto' : ''}`}>

        {/* Left: argument */}
        <div className="lg:col-span-2 flex flex-col gap-8">
          <p className="font-mono text-eyebrow font-semibold tracking-[var(--hero-label-tracking)] uppercase text-font-quaternary">
            {t('contact_label')}
          </p>
          <h2 className="text-[length:var(--text-section-title)] font-normal tracking-[-0.02em] leading-[1.1] text-font-primary">
            {t('title')}
          </h2>

          <a
            href={`mailto:${tCommon('email')}`}
            className="mt-6 inline-flex items-center gap-3 text-nav text-font-primary/80 hover:text-font-primary transition-colors w-fit"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/icons/sms-notification.svg" alt="" className="size-5 invert opacity-60" />
            {tCommon('email')}
          </a>

          <p className="text-nav text-font-primary/40 mt-2">
            {t('footer_note')}
          </p>
        </div>

        {/* Right: Cal.com inline embed + visible fallbacks for blocked
            iframes (firewalls in hospitals/firms sometimes strip cal.com). */}
        <div className="lg:col-span-3 flex flex-col gap-3">
          <div
            className="rounded-[var(--border-radius-primary)] overflow-hidden bg-white/5 border border-white/10"
            style={{ minHeight: 760 }}
          >
            <noscript>
              {/* When JS is off the observer never fires and onLoad won't
                  resolve; hide the live container and show the link fallback. */}
              <style>{`.cal-iframe-content{display:none}`}</style>
              <p className="px-4 py-3 text-nav text-font-primary/70">
                {t('form_subtitle')}{' '}
                <a href={CAL_URL} target="_blank" rel="noopener noreferrer" className="underline text-font-primary">
                  {t('form_submit')}
                </a>
              </p>
            </noscript>
            <div
              ref={wrapperRef}
              className="cal-iframe-content relative w-full"
              style={{ minHeight: 760 }}
            >
              {shouldMountIframe && !iframeLoaded && (
                <div
                  className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
                  role="status"
                  aria-label={t('form_title')}
                >
                  <div className="size-10 border-2 border-white/15 border-t-white/70 rounded-full animate-spin" />
                </div>
              )}
              {shouldMountIframe ? (
                <iframe
                  src={embedSrc}
                  title={t('form_title')}
                  className="w-full block"
                  style={{ height: '760px', minHeight: '600px', border: 0 }}
                  referrerPolicy="no-referrer-when-downgrade"
                  onLoad={() => setIframeLoaded(true)}
                />
              ) : (
                <div aria-hidden="true" className="w-full h-[760px]" />
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 px-1 text-nav">
            <a
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1.5 text-font-primary/70 hover:text-font-primary underline underline-offset-2 decoration-white/20 hover:decoration-white/50 transition-colors w-fit"
            >
              <span>{t('fallback_link')}</span>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/icons/arrow-right-full.svg" alt="" className="size-4 -rotate-45 invert opacity-70 group-hover:opacity-100 transition-opacity" />
            </a>
            <a
              href={`mailto:${tCommon('email')}`}
              className="group inline-flex items-center gap-1.5 text-font-primary/65 hover:text-font-primary underline underline-offset-2 decoration-white/20 hover:decoration-white/50 transition-colors w-fit"
            >
              <span>{t('send_question')}</span>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/icons/arrow-right-full.svg" alt="" className="size-4 -rotate-45 invert opacity-70 group-hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
